/* eslint-disable @typescript-eslint/no-explicit-any */
import { ReactNode } from 'react';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';

import { CircleIcon } from '../../Icons';

import { Color, cn } from 'styles';
import { RadioGroupProps, useRadioGroup } from './Group';

const colorClasses = {
  primary:
    'data-[state=checked]:text-primary-foreground data-[state=checked]:border-primary group-data-[state=checked]:fill-primary',
  secondary:
    'data-[state=checked]:text-secondary-foreground data-[state=checked]:border-secondary group-data-[state=checked]:fill-secondary',
  success:
    'data-[state=checked]:text-success-foreground data-[state=checked]:border-success group-data-[state=checked]:fill-success',
  destructive:
    'data-[state=checked]:text-destructive-foreground data-[state=checked]:border-destructive group-data-[state=checked]:fill-destructive',
  info: 'data-[state=checked]:text-info-foreground data-[state=checked]:border-info group-data-[state=checked]:fill-info',
};

const radioSizeConfig = {
  small: {
    sizeClass: 'h-4 w-4',
    iconSize: 12,
  },

  medium: {
    sizeClass: 'h-5 w-5',
    iconSize: 14,
  },

  large: {
    sizeClass: 'h-6 w-6',
    iconSize: 16,
  },
};

export type RadioProps = {
  className?: string;

  color?: Extract<Color, 'primary' | 'secondary' | 'success' | 'destructive' | 'info'>;

  size?: RadioGroupProps['size'];

  icon?: ReactNode;
  iconSize?: number;

  id?: string;
  value: string;
  label?: ReactNode;

  addDot?: boolean;

  disabled?: boolean;

  render?: (params: { radioDot: ReactNode }) => ReactNode;
};

export function Radio({
  className,
  iconSize,
  icon,
  color,
  size,
  render,
  label,
  addDot = true,
  ...props
}: RadioProps) {
  const parentConfig = useRadioGroup();

  const sizeConfig = radioSizeConfig[size || parentConfig.size];

  const actualColor = color || parentConfig.color;

  const dot = (
    <RadioGroupPrimitive.Indicator
      forceMount
      className={cn(
        'inline-flex items-center justify-center text-current',
        'aspect-square rounded-full border text-primary disabled:cursor-not-allowed disabled:opacity-50',
        colorClasses[actualColor],
        sizeConfig.sizeClass,
      )}
    >
      {icon || (
        <CircleIcon
          className={colorClasses[actualColor]}
          color="transparent"
          size={iconSize || sizeConfig.iconSize}
        />
      )}
    </RadioGroupPrimitive.Indicator>
  );

  return (
    <RadioGroupPrimitive.Item
      {...props}
      className={cn(
        'peer shrink-0',
        'group ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        className,
      )}
    >
      {render?.({ radioDot: dot }) ?? (
        <span className="inline-flex items-center gap-2">
          {addDot && dot}
          {label}
        </span>
      )}
    </RadioGroupPrimitive.Item>
  );
}
